import { Alert, EmptyState, MoneyText, PageHeader, SkeletonRows } from "@station/ui";
import { createFileRoute, Link } from "@tanstack/react-router";
import { type ReactElement, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { useFinanceOverview } from "../../../features/reports/queries.js";
import {
  ReportFilterBar,
  ReportsGate,
  reportSearchShape,
  SectionCard,
  StatTile,
  TableScroll,
  useReportFilter,
  useScopeOptions,
} from "../../../features/reports/shared.js";
import { errorMessage } from "../../../lib/errors.js";
import { formatDate } from "../../../lib/format.js";
import { useDocumentTitle } from "../../../lib/hooks.js";

const searchSchema = z.object(reportSearchShape);

export const Route = createFileRoute("/_app/raporlar/finans")({
  validateSearch: (search) => searchSchema.parse(search),
  component: FinanceRoute,
});

/**
 * Nakit ve finans (Prompt 22, ADR 0022 §10): şirketin ANLIK durumu — nakit pozisyonu (kasa, banka + kullanılabilir KMH, POS'ta
 * bekleyen, bugünkü net hareket), çek / senet portföyü ve vade kovaları, alacak yaşlandırma. Sahip modüllerin canlı rapor
 * servislerinden; her bölüm kaynağın izniyle (izinsiz bölüm gizli).
 */
function FinanceRoute(): ReactElement {
  const { t } = useTranslation();
  useDocumentTitle(t("menu.reportsFinance"));
  return (
    <>
      <PageHeader title={t("menu.reportsFinance")} description={t("reports.finance.description")} />
      <ReportsGate>
        <Finance />
      </ReportsGate>
    </>
  );
}

function Th({ children, align = "left" }: { children: ReactNode; align?: "left" | "right" }): ReactElement {
  return (
    <th scope="col" className={`px-3 py-2 font-medium ${align === "right" ? "text-right" : "text-left"}`}>
      {children}
    </th>
  );
}

function Finance(): ReactElement {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const [filter, setFilter, explicit] = useReportFilter(search, "TODAY");
  const options = useScopeOptions();
  const companyId = filter.companyId ?? options.companies[0]?.id ?? null;
  const overview = useFinanceOverview(companyId, companyId !== null);
  const data = overview.data;

  return (
    <div className="grid gap-6">
      <ReportFilterBar
        reportKey="FINANCE_OVERVIEW"
        state={{ ...filter, companyId }}
        onChange={setFilter}
        stations="none"
        range={false}
        companyRequired
        maxDays={366}
        explicit={explicit}
      />
      {overview.error ? <Alert tone="danger">{errorMessage(overview.error)}</Alert> : null}
      {!data ? (
        overview.isPending ? (
          <SkeletonRows rows={6} />
        ) : null
      ) : !data.cash && !data.cheques && !data.receivables ? (
        <EmptyState
          title={t("reports.finance.emptyTitle")}
          description={t("reports.finance.emptyDescription")}
        />
      ) : (
        <>
          <p className="text-sm text-muted-foreground">
            {t("reports.finance.asOf", { company: data.companyName, date: formatDate(data.asOf) })}
          </p>
          {data.cash ? (
            <SectionCard
              title={t("reports.finance.cashTitle")}
              actions={
                <Link
                  to="/finans"
                  className="text-sm font-medium text-primary underline-offset-4 hover:underline"
                >
                  {t("reports.finance.openTreasury")}
                </Link>
              }
            >
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
                <StatTile
                  label={t("reports.finance.cash")}
                  value={<MoneyText value={data.cash.cash} colorize />}
                />
                <StatTile
                  label={t("reports.finance.bank")}
                  value={<MoneyText value={data.cash.bank} colorize />}
                />
                <StatTile
                  label={t("reports.finance.bankAvailable")}
                  value={<MoneyText value={data.cash.bankAvailable} colorize />}
                  hint={t("reports.finance.bankAvailableHint")}
                />
                <StatTile
                  label={t("reports.finance.posPending")}
                  value={<MoneyText value={data.cash.posPending} />}
                />
                <StatTile
                  label={t("reports.finance.netToday")}
                  value={<MoneyText value={data.cash.netToday} colorize />}
                />
              </div>
            </SectionCard>
          ) : null}
          {data.cheques ? (
            <SectionCard
              title={t("reports.finance.chequesTitle")}
              actions={
                <Link
                  to="/cek-senet"
                  className="text-sm font-medium text-primary underline-offset-4 hover:underline"
                >
                  {t("reports.finance.openInstruments")}
                </Link>
              }
            >
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <StatTile
                  label={t("reports.finance.receivableOpen")}
                  value={<MoneyText value={data.cheques.receivableOpen} />}
                />
                <StatTile
                  label={t("reports.finance.endorsedOpen")}
                  value={<MoneyText value={data.cheques.endorsedOpen} />}
                />
                <StatTile
                  label={t("reports.finance.problemOpen")}
                  value={<MoneyText value={data.cheques.problemOpen} />}
                />
                <StatTile
                  label={t("reports.finance.issuedOpen")}
                  value={<MoneyText value={data.cheques.issuedOpen} />}
                />
              </div>
              <TableScroll label={t("reports.finance.maturityTitle")}>
                <table className="w-full text-sm">
                  <caption className="sr-only">{t("reports.finance.maturityTitle")}</caption>
                  <thead className="bg-muted/50">
                    <tr>
                      <Th>{t("reports.finance.maturity")}</Th>
                      <Th align="right">{t("reports.finance.receivable")}</Th>
                      <Th align="right">{t("reports.finance.payable")}</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.cheques.maturity.map((row) => (
                      <tr key={row.bucket} className="border-t">
                        <td className="px-3 py-2">{row.label}</td>
                        <td className="px-3 py-2 text-right">
                          <MoneyText value={row.receivable} />
                        </td>
                        <td className="px-3 py-2 text-right">
                          <MoneyText value={row.payable} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableScroll>
            </SectionCard>
          ) : null}
          {data.receivables ? (
            <SectionCard
              title={t("reports.finance.receivablesTitle")}
              description={t("reports.finance.receivablesDescription")}
              actions={
                <Link
                  to="/cari"
                  className="text-sm font-medium text-primary underline-offset-4 hover:underline"
                >
                  {t("reports.finance.openCounterparties")}
                </Link>
              }
            >
              <TableScroll label={t("reports.finance.agingTitle")}>
                <table className="w-full min-w-[600px] text-sm">
                  <caption className="sr-only">{t("reports.finance.agingTitle")}</caption>
                  <thead className="bg-muted/50">
                    <tr>
                      {data.receivables.bucketLabels.map((label) => (
                        <Th key={label} align="right">
                          {label}
                        </Th>
                      ))}
                      <Th align="right">{t("reports.columns.total")}</Th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-t">
                      {data.receivables.totals.map((value, index) => (
                        <td
                          key={data.receivables!.bucketLabels[index] ?? index}
                          className="px-3 py-2 text-right"
                        >
                          <MoneyText value={value} />
                        </td>
                      ))}
                      <td className="px-3 py-2 text-right font-semibold">
                        <MoneyText value={data.receivables.grandTotal} />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </TableScroll>
              {data.receivables.top.length > 0 ? (
                <TableScroll label={t("reports.finance.topTitle")}>
                  <table className="w-full text-sm">
                    <caption className="sr-only">{t("reports.finance.topTitle")}</caption>
                    <thead className="bg-muted/50">
                      <tr>
                        <Th>{t("reports.finance.counterparty")}</Th>
                        <Th align="right">{t("reports.finance.openReceivable")}</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.receivables.top.map((row) => (
                        <tr key={row.counterpartyId} className="border-t">
                          <td className="px-3 py-2">{row.name}</td>
                          <td className="px-3 py-2 text-right">
                            <MoneyText value={row.total} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </TableScroll>
              ) : null}
            </SectionCard>
          ) : null}
        </>
      )}
    </div>
  );
}
